'use client'

import Link from 'next/link'
import Tag from 'src/components/design-system/Tag'
import Typography from 'src/components/design-system/Typography'
import { useTranslateEntry } from 'src/i18n/client/useTranslateEntry'
import { formatDisplayTitle } from 'src/_helpers/formatDisplayTitle'
import PlusCircle from 'public/icons/interface/plus-circle.svg'
import FormattedTitle from 'src/components/FormattedTitle'
import { titleToText } from 'src/_helpers/titleMarkup'

const JournalArticleItem = ({
  date,
  title,
  desc,
  author,
  additional,
  url,
  tags = [],
  classCustom = '',
  variant = '',
}) => {
  const translate = useTranslateEntry()
  const displayTitle = formatDisplayTitle(titleToText(title))
  const ariaLabel = displayTitle || desc || date || 'Journal article item'
  const showTitle = Boolean(title && title !== desc)
  const hasTags = Array.isArray(tags) && tags.length > 0
  const isPrimaryBackground = variant === 'primary-background'
  const isJournalDomain = variant === 'journal-domain'
  const dividerColor = isJournalDomain
    ? 'bg-basic-gray7'
    : isPrimaryBackground
      ? 'bg-basic-gray8'
      : variant === 'white'
        ? 'bg-dark-blue-dark-blue6'
        : 'bg-dark-blue-dark-blue5'
  const mutedTextColor = isJournalDomain
    ? 'text-basic-black'
    : isPrimaryBackground || variant === 'white'
      ? 'text-basic-gray2'
      : 'text-dark-blue-dark-blue2'
  const ctaColorClass = isJournalDomain ? 'text-basic-black' : 'text-dark-blue-dark-blue2'
  const Wrapper = url ? Link : 'div'

  return (
    <Wrapper
      {...(url ? { href: url } : {})}
      className={`group relative flex min-h-full flex-col focus-visible:outline focus-visible:outline-offset-4 focus-visible:outline-functional-focus-color cursor-pointer ${classCustom}`}
      {...(url ? { 'aria-label': ariaLabel } : {})}
    >
      {date && (
        <>
          <Typography variant="body1" className="font-semibold pb-24">
            {date}
          </Typography>
          <div className={`h-px w-full ${dividerColor} mb-12`} aria-hidden="true" />
        </>
      )}

      {hasTags && (
        <div className="flex flex-wrap items-center gap-6 pb-24 transition-opacity duration-200 ease-out supports-hover:group-hover:opacity-0 supports-hover:group-hover:pointer-events-none group-focus-within:opacity-0 group-focus-within:pointer-events-none">
          {tags.map((tag, index) => (
            <Tag
              key={`${tag?.label || 'tag'}-${index}`}
              type={tag?.type}
              label={tag?.label}
              className={
                isJournalDomain
                  ? 'bg-basic-gray8 text-basic-black [&_.tag__dot]:bg-basic-black'
                  : tag?.type === 'new'
                    ? variant === 'white'
                      ? 'bg-green-green5 text-basic-black [&_.tag__dot]:bg-green-green2'
                      : 'bg-green-green4 text-basic-black [&_.tag__dot]:bg-green-green1'
                    : variant === 'white'
                      ? 'bg-basic-gray8 text-basic-black'
                      : 'bg-blueish-blueish3 text-basic-black'
              }
            />
          ))}
        </div>
      )}

      <div
        className={`flex flex-col transition-transform duration-300 ease-out ${
          hasTags ? 'supports-hover:group-hover:-translate-y-48 group-focus-within:-translate-y-48' : ''
        }`}
      >
        {additional && (
          <Typography variant="body4" component="p" color={mutedTextColor} className={'pb-12'}>
            {additional}
          </Typography>
        )}

        {showTitle && (
          <Typography variant="body1" className="font-semibold" component="h3">
            <FormattedTitle value={title} />
          </Typography>
        )}
        {desc && (
          <Typography variant="body1" className="font-semibold" component="h3">
            {desc}
          </Typography>
        )}
        {author && (
          <Typography
            variant="body4"
            component="p"
            color={mutedTextColor}
            className={
              'pt-24 transition-[opacity,transform] duration-300 ease-out supports-hover:group-hover:-translate-y-4 supports-hover:group-hover:opacity-0 supports-hover:group-hover:pointer-events-none group-focus-within:-translate-y-4 group-focus-within:opacity-0 group-focus-within:pointer-events-none'
            }
          >
            {author}
          </Typography>
        )}
        <span
          aria-hidden="true"
          className={`mt-24 inline-flex items-center gap-4 font-serif font-bold text-buttons-m leading-100 opacity-0 pointer-events-none translate-y-4 transition-[opacity,transform,color] duration-300 ease-out supports-hover:group-hover:opacity-100 supports-hover:group-hover:pointer-events-auto supports-hover:group-hover:translate-y-0 group-focus-within:opacity-100 group-focus-within:pointer-events-auto group-focus-within:translate-y-0 ${ctaColorClass}`}
        >
          <span>{translate('T_SEE_MORE')}</span>
          <PlusCircle />
        </span>
      </div>
    </Wrapper>
  )
}

export { JournalArticleItem }
